import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import * as authApi from '../api/auth';
import { isNativeFirstRun, isInShell, requestAppleSignIn, requestGoogleSignIn } from '../shell/bridge';
import { LoginPage } from '@shared-ui/login';
import './Login.css';
import { useTenant } from '@shared-ui/tenant';
import { peekReturnTo } from '../utils/returnTo';
import { teacherSiteUrl } from '@shared-ui/front-door';
import { page } from '../appPagesLoader';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID_WEB || '';

type Mode = 'login' | 'register';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, phoneLogin, phoneRegister, googleLogin, appleLogin, startGuest } = useAuth();

  // The app's pages arrive while the user signs in, not after.
  useEffect(() => { page.preload(); }, []);

  // Check for invite query param — auto-switch to register mode with prefilled email.
  // `?register=1` (used by the first-run win-moment "create account" CTA) also opens register.
  const searchParams = new URLSearchParams(location.search);
  const inviteEmail = searchParams.get('invite');
  const wantRegister = !!inviteEmail || searchParams.get('register') === '1';
  // `?login=` — sent here from the teacher app's wrong-door notice, with the
  // identifier the person already typed there. Prefilled, never locked.
  const loginHint = searchParams.get('login');

  const [mode, setMode] = useState<Mode>(wantRegister ? 'register' : 'login');

  // Self-serve signup reaches the public tenant only; a tenant URL is
  // invite-only, so it offers no register link. Completing an invite
  // (`?invite=`) still works — that is what `wantRegister` above covers.
  const { tenant } = useTenant();
  const selfRegistration = !tenant || tenant.allowsSelfRegistration;

  // "Try without signing up" (deferred registration) — only shown when the
  // day_one_anonymous gate is on. An anonymous visitor has no stored gates,
  // so we ask the server.
  const [guestEnabled, setGuestEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    authApi.guestAvailable().then((ok) => { if (!cancelled) setGuestEnabled(ok); });
    return () => { cancelled = true; };
  }, []);

  // Phone + OTP login availability (master kill-switch; ships dark).
  const [phoneEnabled, setPhoneEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    authApi.phoneLoginAvailable().then((ok) => { if (!cancelled) setPhoneEnabled(ok); });
    return () => { cancelled = true; };
  }, []);

  // The stored destination first: it is the only one carrying the query string,
  // and the only one that survives a reload or an OAuth round trip. Router state
  // is the fallback for every other entry to this page. See utils/returnTo.ts.
  const from = peekReturnTo()
    ?? (location.state as { from?: { pathname: string } })?.from?.pathname
    ?? '/home';

  // Native Sign in with Apple — only inside the iOS shell (the shell runs the system
  // sheet and hands back the identity token, which we POST to /auth/apple).
  const handleAppleLogin = async () => {
    const cred = await requestAppleSignIn();
    await appleLogin(cred.identityToken, { givenName: cred.givenName, familyName: cred.familyName });
    navigate(from, { replace: true });
  };

  const handleTryGuest = async () => {
    await startGuest();
    navigate('/first-run', { replace: true });
  };

  const handleLogin = async (email: string, password: string) => {
    await login({ email, password });
    navigate(from, { replace: true });
  };

  const handleRegister = async (name: string, email: string, password: string, verificationToken: string) => {
    await register({ name, email, password, verificationToken });
    navigate(from, { replace: true });
  };

  const handleGoogleLogin = async (credential: string) => {
    const { needsRegistration } = await googleLogin(credential);
    navigate(needsRegistration ? '/complete-registration' : from, { replace: true });
  };

  // Native Google sign-in — only inside the iOS shell (Google's OAuth is blocked
  // in the embedded WebView, so the shell runs it in a system browser and hands
  // back an id_token, which we POST to /auth/google with clientType 'ios').
  const handleGoogleNativeLogin = async () => {
    const { idToken } = await requestGoogleSignIn();
    const { needsRegistration } = await googleLogin(idToken, { native: true });
    navigate(needsRegistration ? '/complete-registration' : from, { replace: true });
  };

  const handleSendOtp = async (identifier: string, purpose: 'register' | 'reset-password' | 'login', channel: 'email' | 'phone' = 'email') => {
    await authApi.sendOtp(identifier, purpose, channel);
  };

  const handleVerifyOtp = async (identifier: string, code: string, purpose: 'register' | 'reset-password' | 'login', channel: 'email' | 'phone' = 'email') => {
    return authApi.verifyOtp(identifier, code, purpose, channel);
  };

  const handleResetPassword = async (verificationToken: string, newPassword: string) => {
    await authApi.resetPassword(verificationToken, newPassword);
  };

  const handlePhoneLogin = async (phone: string, verificationToken: string) => {
    await phoneLogin(phone, verificationToken);
    navigate(from, { replace: true });
  };

  const handlePhoneRegister = async (name: string, phone: string, verificationToken: string, password?: string) => {
    await phoneRegister(name, phone, verificationToken, password);
    navigate(from, { replace: true });
  };

  return (
    <LoginPage
      subtitle="Sign in to continue learning"
      registerTitle="Create your student account"
      registerSubtitle="Practise questions for your class, with feedback on every step."
      mode={mode}
      onLogin={handleLogin}
      onRegister={handleRegister}
      onGoogleLogin={handleGoogleLogin}
      onGoogleNativeLogin={isInShell() ? handleGoogleNativeLogin : undefined}
      onAppleLogin={isInShell() ? handleAppleLogin : undefined}
      onSendOtp={handleSendOtp}
      onVerifyOtp={handleVerifyOtp}
      onResetPassword={handleResetPassword}
      phoneLoginEnabled={phoneEnabled}
      onPhoneLogin={handlePhoneLogin}
      onPhoneRegister={handlePhoneRegister}
      googleClientId={GOOGLE_CLIENT_ID}
      defaultEmail={inviteEmail || loginHint || undefined}
      emailDisabled={!!inviteEmail}
      inviteBrandName={searchParams.get('tenant') || undefined}
      footer={
        <>
          {selfRegistration && (
            <p className="register-link">
              New to BuzzBrain? <a href="#" onClick={(e) => { e.preventDefault(); setMode('register'); }}>Register here</a>
            </p>
          )}
          {/* Inside the native shell the FRE "Try a … question" screen is the
              guest entry point, so don't re-offer it here — a user who chose
              "Login to Continue" must not loop back to the guest option.
              Also hidden on a tenant URL: a guest lands in the public tenant,
              so offering it under a school's brand promises entry to the
              school and delivers something else. */}
          {guestEnabled && !isNativeFirstRun() && selfRegistration && (
            <p className="register-link">
              Just want to try it? <a href="#" onClick={(e) => { e.preventDefault(); handleTryGuest(); }}>Try without signing up</a>
            </p>
          )}
          {!tenant && !isInShell() && (
            <p className="login-other-door">
              Are you a teacher? <a href={`${teacherSiteUrl()}/login`}>Teacher sign-in</a>
            </p>
          )}
        </>
      }
      registerFooter={!tenant && !isInShell() && !inviteEmail && (
        <p className="login-other-door">
          Are you a teacher? <a href={`${teacherSiteUrl()}/login?register=1`}>Teacher sign-up</a>
        </p>
      )}
    />
  );
}
